import {
  IHolisticRubricDescription,
  IAnalyticRubricCriterion,
} from '@/components/staffer/ExerciseBuilder/Rubric/useQuestionRubric';
import axios from 'axios';
import * as Routes from '@/modules/routes';

/**
 * Checks if a string is empty after stripping HTML tags
 * Froala editor can have empty content like '<p><br></p>'
 */
export const isEmptyContent = (html: string | null | undefined): boolean => {
  if (!html) return true;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const textContent = doc.body.textContent?.trim() ?? '';
  return textContent.length === 0;
};

/**
 * Returns array of description_ids for rows with empty descriptions
 * Used for validation to check if rubric is complete
 */
export const getEmptyHolisticCellCount = (
  data: IHolisticRubricDescription[],
): number => data
  .filter(item => isEmptyContent(item.description_en)).length;

/**
 * Returns number of empty criterion or description cells
 * Used for validation to check if rubric is complete
 */
export const getEmptyAnalyticCellCount = (
  data: IAnalyticRubricCriterion[],
): number => {
  let emptyCriterionNames: number = 0;
  let emptyLevelDescriptions: number = 0;

  data.forEach((criterion) => {
    if (isEmptyContent(criterion.name_en)) {
      emptyCriterionNames += 1;
    }

    criterion.levels.forEach((level) => {
      if (isEmptyContent(level.description_en)) {
        emptyLevelDescriptions += 1;
      }
    });
  });

  return emptyCriterionNames + emptyLevelDescriptions;
};

export const allCriterionNamesUnique = (data: IAnalyticRubricCriterion[]): boolean => {
  const names = data.map(c => c.name_en.trim().toLowerCase());
  const uniqueNames = new Set(names);
  return uniqueNames.size === names.length;
};

export const createRubric = async (
  questionId : number,
  rubricType: 'holistic' | 'analytic',
  rubricData : IHolisticRubricDescription[] | IAnalyticRubricCriterion[],
  autoTranslate : boolean,
) => {
  const data = {
    automatic_translation_enabled: autoTranslate,
    name: rubricType === 'holistic' ? 'Holistic Rubric' : 'Analytic Rubric',
    question_id: questionId,
    rubric_data: rubricData,
    rubric_type: rubricType,
  };

  const response = await axios.post(Routes.plato_api_rubrics_path(), data);
  return response.data;
};

export const attachRubric = async (rubricId: number, questionId: number) => {
  const response = await axios.post(Routes.plato_api_attach_rubric_path(rubricId), {
    question_id: questionId,
  });
  return response.data;
};

export const updateRubric = async (
  rubricId : number | undefined,
  rubricType: 'holistic' | 'analytic',
  rubricData : IHolisticRubricDescription[] | IAnalyticRubricCriterion[],
  autoTranslate : boolean,
) => {
  const data = {
    automatic_translation_enabled: autoTranslate,
    name: rubricType === 'holistic' ? 'Holistic Rubric' : 'Analytic Rubric',
    rubric_data: rubricData,
    rubric_type: rubricType,
  };

  const response = await axios.put(Routes.plato_api_rubric_path(rubricId), data);
  return response.data;
};
